import { getEnvironment, isCypressTest, isProd } from "./environment";
import { Role } from "../types/auth";

// The Datadog SDKs are imported dynamically at init() so they stay out of the
// eager bundle — this module is reachable from the app entrypoint, and a static
// import here would pull the whole vendor-datadog chunk into the critical path.
type DatadogLogs = (typeof import("@datadog/browser-logs"))["datadogLogs"];
type DatadogRum = (typeof import("@datadog/browser-rum"))["datadogRum"];

const clientToken = "pubfc50a230fa18be59172dac9542f089c2";
const site = "datadoghq.com";
const env = getEnvironment();

/**
 * Session replay sample rates by environment.
 *   production  – 100 % so every feedback-tagged session has a replay available.
 *   staging     – 100 % for full visibility during QA.
 *   development – 0 % to avoid noise and local overhead.
 */
const sessionReplaySampleRate = env === "development" ? 0 : 100;

export type LoggingUser = {
  id: string;
  name?: string;
  brand_id?: number;
  brand_name?: string;
  rmn_publisher_id?: number;
  role: Role | string;
  external_info?: {
    role?: string;
    is_agency?: boolean;
    seniority?: string;
  };
};

// Dunder Mifflin (Test): automated end-to-end runs log in here as a customer.
const TEST_BRAND_ID = 4;

// Suffix of the customer-role logins Adora staff use to test the customer experience.
const STAFF_USERNAME_SUFFIX = "@adora";

export type UserAudience = "customer" | "internal" | "rmn_publisher" | "unknown";

/**
 * Who a user is for analytics, as opposed to what their role lets them do. Mirrors
 * delorean's resolve_user_audience so RUM and backend metrics filter the same people:
 * Adora staff test the customer experience from customer-role logins named
 * c-<name>@adora, real customer usernames never end in "@adora", and the test
 * brand is never customer traffic.
 */
export const resolveUserAudience = (user: LoggingUser): UserAudience => {
  if (user.role === "internal") return "internal";
  if (user.role === "rmn_publisher") return "rmn_publisher";
  if (user.role !== "customer") return "unknown";
  return user.name?.endsWith(STAFF_USERNAME_SUFFIX) || user.brand_id === TEST_BRAND_ID ? "internal" : "customer";
};

const DataDogManager = (() => {
  let isReady = false;
  // Whether a user has been set, which is what a user property needs to attach to.
  // Distinct from `isReady` (SDK loaded) and `isRumInitialized` (RUM started).
  let hasUser = false;
  let isRumInitialized = false;
  let initRum = false;
  let pendingOperations: Array<() => void> = [];
  // Properties set before the user is known. `setUser` replaces the whole user context,
  // so a property written before it would be wiped; one written before the SDK knows any
  // user at all has nowhere to land yet. Held here and applied once it does, rather than
  // dropped -- a dropped property is never retried, and the caller is not told.
  const pendingUserProperties = new Map<string, string | number | boolean>();
  let service = "web"; // default to web for backwards compatibility
  const version = String(import.meta.env.VITE_APP_VERSION || "dev");
  // Loaded by init(); null until the dynamic import resolves.
  let logsSdk: DatadogLogs | null = null;
  let rumSdk: DatadogRum | null = null;
  let loadFailed = false;

  const runOrQueue = (operation: () => void) => {
    if (loadFailed) {
      // SDK chunk never loaded — drop instead of queueing forever.
      return;
    }

    if (!isReady) {
      pendingOperations.push(operation);
      return;
    }

    operation();
  };

  const flushPendingOperations = () => {
    const operations = pendingOperations;
    pendingOperations = [];

    operations.forEach((operation) => operation());
  };

  const isRealUser = (user: LoggingUser): boolean => {
    const isWebCustomer = user.role === "customer" && user.brand_id !== TEST_BRAND_ID; // TODO: Make this more robust
    const isMediaUser = user.role === "rmn_publisher";
    const isInternalUser = user.role === "internal";

    return (
      (isProd() && !isCypressTest() && (isWebCustomer || isMediaUser || isInternalUser)) ||
      (!isProd() && user.name === "michael.scott")
    );
  };

  const initLogs = () => {
    logsSdk?.init({
      clientToken,
      site,
      service,
      env,
      version,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      storeContextsAcrossPages: true // fixes issue with version not being sent on browser load events
    });
  };

  const initRUM = () => {
    if (!rumSdk) {
      return;
    }

    rumSdk.init({
      applicationId: "53944f1d-a3ea-4971-822c-098b7b5a245a",
      clientToken,
      site,
      service,
      version,
      env,
      sessionSampleRate: 100,
      sessionReplaySampleRate,
      startSessionReplayRecordingManually: true,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: "allow",
      actionNameAttribute: "data-action-name",
      storeContextsAcrossPages: true // fixes issue with version not being sent on browser load events
    });

    rumSdk.startSessionReplayRecording();
  };

  const init = (serviceName: string, useRum: boolean) => {
    service = serviceName;
    initRum = useRum;

    // Load the SDK chunks lazily; everything called in the meantime is queued
    // and flushed once the SDKs are ready.
    void (async () => {
      try {
        const [logsModule, rumModule] = await Promise.all([
          import("@datadog/browser-logs"),
          useRum ? import("@datadog/browser-rum") : Promise.resolve(null)
        ]);
        logsSdk = logsModule.datadogLogs;
        rumSdk = rumModule ? rumModule.datadogRum : null;
      } catch {
        // SDK chunk failed to load (offline, blocked by extension) —
        // telemetry becomes a no-op rather than breaking the app.
        loadFailed = true;
        pendingOperations = [];
        return;
      }

      initLogs();
      isReady = true;
      flushPendingOperations();
    })();
  };

  const updateUserInfo = (user: LoggingUser) => {
    runOrQueue(() => {
      if (hasUser) {
        return;
      }

      const userWithAudience = { ...user, audience: resolveUserAudience(user) };
      logsSdk?.setUser(userWithAudience);

      if (initRum && isRealUser(user)) {
        if (!isRumInitialized) {
          initRUM();
          isRumInitialized = true;
        }
        rumSdk?.setUser(userWithAudience);
      }

      hasUser = true;

      // After `setUser`, never before: it replaces the user context wholesale.
      pendingUserProperties.forEach((pendingValue, pendingKey) => applyUserProperty(pendingKey, pendingValue));
      pendingUserProperties.clear();
    });
  };

  const applyUserProperty = (key: string, value: string | number | boolean) => {
    logsSdk?.setUserProperty(key, value);
    if (isRumInitialized) {
      rumSdk?.setUserProperty(key, value);
    }
  };

  const setUserProperty = (key: string, value: string | number | boolean) => {
    runOrQueue(() => {
      if (!hasUser) {
        pendingUserProperties.set(key, value);
        return;
      }

      applyUserProperty(key, value);
    });
  };

  // ---------------------------------------------------------------------------
  // Custom Reporting – safe to call before RUM init (queued until the SDK chunk
  // loads, then the DD SDK buffers internally until RUM initializes).
  // Guarded by `initRum` so we don't buffer actions for scenarios where RUM
  // was never requested.
  // ---------------------------------------------------------------------------

  /** Record a timing milestone relative to the current view start. */
  const reportTiming = (name: string) => {
    if (!initRum) return;
    // Capture the timestamp at call time — a queued call executing after the
    // SDK chunk loads would otherwise record the flush time, not the milestone.
    const time = Date.now();
    runOrQueue(() => rumSdk?.addTiming(name, time));
  };

  /** Report a structured custom action with arbitrary context. */
  const reportCustomAction = (name: string, context?: Record<string, unknown>) => {
    if (!initRum) return;
    runOrQueue(() => rumSdk?.addAction(name, context));
  };

  // ---------------------------------------------------------------------------
  // Logging – sends structured logs to Datadog for debugging and diagnostics.
  // Safe to call before init (queued until SDK loads).
  // ---------------------------------------------------------------------------

  /** Log an info-level message with optional context. */
  const logInfo = (message: string, context?: Record<string, unknown>) => {
    runOrQueue(() => logsSdk?.logger.info(message, context));
  };

  /** Log a warning-level message with optional context. */
  const logWarn = (message: string, context?: Record<string, unknown>) => {
    runOrQueue(() => logsSdk?.logger.warn(message, context));
  };

  /** Log an error-level message with optional context and error object. */
  const logError = (message: string, context?: Record<string, unknown>, error?: Error) => {
    runOrQueue(() => logsSdk?.logger.error(message, context, error));
  };

  /** Log a debug-level message with optional context. */
  const logDebug = (message: string, context?: Record<string, unknown>) => {
    runOrQueue(() => logsSdk?.logger.debug(message, context));
  };

  return {
    init,
    updateUserInfo,
    setUserProperty,
    reportTiming,
    reportCustomAction,
    logInfo,
    logWarn,
    logError,
    logDebug
  };
})();

export default DataDogManager;
